import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config/api';
import { getThumbnailUrl } from '../lib/utils';
import {
  Heart,
  BookOpen, 
  Award, 
  Clock,
  Trash2,
  Loader2,
  ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
const API = `${API_BASE}/api`;

export default function WishlistPage() {
  const { token } = useAuth();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);

  useEffect(() => {
    if (token) {
      fetchWishlist();
    }
  }, [token]);

  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API}/user/wishlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(response.data.wishlist || []);
    } catch (error) {
      console.error('Failed to fetch wishlist:', error);
      toast.error('Failed to load wishlist');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (courseId) => {
    setRemoving(courseId);
    try {
      await axios.delete(`${API}/user/wishlist/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(prev => prev.filter(item => item.course_id !== courseId));
      toast.success('Removed from wishlist');
    } catch (error) {
      toast.error('Failed to remove from wishlist');
    } finally {
      setRemoving(null);
    }
  };

  if (!token) {
    return (
      <Layout>
        <div className="min-h-screen flex flex-col items-center justify-center">
          <Heart className="h-16 w-16 text-zinc-600 mb-4" />
          <h2 className="text-xl text-white mb-2">Please Log In</h2>
          <p className="text-zinc-400 mb-4">Log in to view your learning wishlist</p>
          <Link to="/login">
            <Button className="bg-navigate-gold hover:bg-navigate-gold/90 text-black">
              Log In
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 text-navigate-gold animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-white flex items-center gap-3">
            <Heart className="h-8 w-8 text-red-400 fill-red-400" />
            My Learning Wishlist
          </h1>
          <p className="text-zinc-400 mt-2">
            Courses you've saved for later
          </p>
        </div>

        {wishlist.length === 0 ? (
          <div className="text-center py-16 bg-zinc-800/30 rounded-xl border border-zinc-700">
            <Heart className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
            <h2 className="text-xl text-white mb-2">Your Wishlist is Empty</h2>
            <p className="text-zinc-400 mb-6">Browse courses and click the heart icon to add them here</p>
            <Link to="/courses">
              <Button className="bg-navigate-gold hover:bg-navigate-gold/90 text-black">
                Browse Courses
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid gap-4">
            {wishlist.map(item => {
              const course = item.course;
              if (!course) return null;
              
              return (
                <div 
                  key={item.wishlist_id}
                  className="bg-zinc-800/50 border border-zinc-700 rounded-xl p-4 flex items-center gap-4 hover:border-zinc-600 transition-colors"
                >
                  {/* Thumbnail */}
                  <div className="w-20 h-20 lg:w-28 lg:h-20 rounded-lg bg-zinc-700 overflow-hidden shrink-0">
                    {course.thumbnail_url ? (
                      <img 
                        src={getThumbnailUrl(course.thumbnail_url)} 
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="h-8 w-8 text-zinc-500" />
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-navigate-gold uppercase tracking-wider">
                      {course.category}
                    </span>
                    <h3 className="text-white font-semibold mt-1 truncate">{course.title}</h3>
                    <div className="flex items-center gap-4 mt-2 text-sm text-zinc-400">
                      {course.cpd_points > 0 && (
                        <span className="flex items-center gap-1 text-navigate-gold">
                          <Award className="h-3.5 w-3.5" />
                          {course.cpd_points} CPD
                        </span>
                      )}
                      {course.duration && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {course.duration}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleRemove(course.course_id)}
                      disabled={removing === course.course_id}
                      className="p-2 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="Remove from wishlist"
                    >
                      {removing === course.course_id ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Trash2 className="h-5 w-5" />
                      )}
                    </button>
                    <Link to={`/courses/${course.slug || course.course_id}`}>
                      <Button className="bg-navigate-gold hover:bg-navigate-gold/90 text-black">
                        View Course
                      </Button>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
